import { useState, useMemo, useEffect } from "react";
import SelectBuscable from "../../components/SelectBuscable";   // Fix EE (2026-09-24)
import { createPortal } from "react-dom";
import { Loader2, Plus, X, AlertTriangle } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import MontoInput from "../../components/MontoInput";
import { toast } from "sonner";

/* Fix CO (2026-09-13): el modal de contrato con tercerizados, compartido entre
   Obra → Pagos y Obra → Tareas. Dos pedidos de Edson del 13/09:
   1. "cuando se carga los montos para contrato, debe ser igual al analisis de
      compra de materiales, debe salir ahi monto disponible" — cada tarea muestra
      su fondo de M.O. disponible y avisa cuando el monto cerrado lo supera.
   2. "Desde acá tambien seria bueno que se pueda generar contratos con
      tercerizados" — se abre también desde Tareas, con las tareas marcadas como
      tercerizadas ya tildadas.
   El buscador está porque una obra como INVICTA tiene 1315 tareas: sin filtrar,
   la lista era inusable. */
export default function ContratoTerceroModal({ obraId, tareas = [], preseleccion = [], adendaDe = null, onClose, onSaved }) {
  // Fix EC (2026-09-24): una adenda lleva el contratista del contrato original.
  const [nombre, setNombre] = useState(adendaDe?.contratista?.nombre || "");
  const [ruc, setRuc] = useState(adendaDe?.contratista?.ruc || "");
  // Fix EE (2026-09-24): el contratista se elige del registro de Tesorería → Tercerizados.
  const [tercerizadoId, setTercerizadoId] = useState(adendaDe?.contratista?.tercerizado_id || "");
  const [registro, setRegistro] = useState([]);
  useEffect(() => {
    if (adendaDe) return;
    axios.get(`${API}/tercerizados/registro`, { withCredentials: true })
      .then(({ data }) => setRegistro(data?.tercerizados || []))
      .catch(() => setRegistro([]));
  }, [adendaDe]);
  const [busqueda, setBusqueda] = useState("");
  // Fix EC (2026-09-24): filtro por presupuesto de origen de la tarea.
  const [origen, setOrigen] = useState("todos");
  const origenes = useMemo(() => [...new Set(tareas.map(t => t.item_origen || "Base"))]
    .sort((a, b) => (a === "Base" ? -1 : b === "Base" ? 1 : a.localeCompare(b, "es", { numeric: true }))), [tareas]);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => {
    const ini = {};
    (preseleccion || []).forEach(id => { ini[id] = 0; });
    return ini;
  });

  const toggle = (id) => setSelected(prev => {
    const n = { ...prev };
    if (id in n) delete n[id]; else n[id] = 0;
    return n;
  });

  // Fix DX (2026-09-23): el buscador filtra de verdad. Antes las tareas tildadas se
  // mostraban siempre, así que con "Seleccionar todas" no se podía buscar ninguna.
  // Las tildadas van primero y se muestran hasta 300 filas; el pie suma todas.
  const texto = busqueda.trim().toLowerCase();
  const coinciden = useMemo(() => tareas.filter(t =>
    (origen === "todos" || (t.item_origen || "Base") === origen) && (!texto ||
    [t.item_numero, t.item_descripcion, t.tarea_nombre, t.nivel_label]
      .some(v => String(v || "").toLowerCase().includes(texto)))
  ), [tareas, texto, origen]);
  const visibles = useMemo(() => [
    ...coinciden.filter(t => t.id in selected),
    ...coinciden.filter(t => !(t.id in selected)),
  ].slice(0, 300), [coinciden, selected]);

  // Fix DX (2026-09-23): "Acá por ej, seleccionar todas las tareas. Factor
  // multiplicador: 0.XXXX. Monto cerrado = Disponible x Factor multiplicador"
  // (Edson, 22/09), para repartir un contrato llave en mano entre todas las tareas.
  // "Seleccionar todas" toma las que pasan el buscador, con disponible y que no estén
  // ya en otro contrato (esas se pueden tildar a mano).
  const seleccionables = coinciden.filter(t => (Number(t.fondo_disponible) || 0) > 0 && !t.contrato_tercero);
  const todasTildadas = seleccionables.length > 0 && seleccionables.every(t => t.id in selected);
  const toggleTodas = () => setSelected(prev => {
    const n = { ...prev };
    if (todasTildadas) seleccionables.forEach(t => { delete n[t.id]; });
    else seleccionables.forEach(t => { if (!(t.id in n)) n[t.id] = 0; });
    return n;
  });
  const [factor, setFactor] = useState("");
  const [factorAplicado, setFactorAplicado] = useState(null);
  // Guaraníes enteros. Los que sobran del redondeo van a las líneas con mayor resto,
  // así el total del contrato es exactamente el disponible elegido por el factor.
  const aplicarFactor = () => {
    const f = Number(String(factor).trim().replace(",", "."));
    if (!(f > 0)) { toast.error("Poné un factor mayor a cero (ej. 0,65)"); return; }
    const ids = Object.keys(selected);
    if (!ids.length) { toast.error("Seleccioná al menos una tarea"); return; }
    const porId = new Map(tareas.map(t => [t.id, t]));
    const filas = ids.map(id => {
      const exacto = Math.max(0, (Number(porId.get(id)?.fondo_disponible) || 0) * f);
      const monto = Math.floor(exacto);
      return { id, monto, resto: exacto - monto, exacto };
    });
    let faltan = Math.round(filas.reduce((s, x) => s + x.exacto, 0)) - filas.reduce((s, x) => s + x.monto, 0);
    [...filas].sort((a, b) => b.resto - a.resto).forEach(x => { if (faltan > 0) { x.monto += 1; faltan -= 1; } });
    setSelected(Object.fromEntries(filas.map(x => [x.id, x.monto])));
    setFactorAplicado(f);
  };

  const elegidas = tareas.filter(t => t.id in selected);
  const totalContrato = Object.values(selected).reduce((s, v) => s + (Number(v) || 0), 0);
  const fondoElegido = elegidas.reduce((s, t) => s + (Number(t.fondo_disponible) || 0), 0);
  const excede = totalContrato > fondoElegido + 0.01;

  const submit = async () => {
    const lista = Object.entries(selected).map(([tarea_id, monto_cerrado]) => ({ tarea_id, monto_cerrado: Number(monto_cerrado) || 0 }));
    if (!nombre.trim() || !lista.length) { toast.error("Completá el contratista y seleccioná al menos una tarea"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/obras/${obraId}/contratos-terceros`, { contratista: { nombre, ruc, tercerizado_id: tercerizadoId || null }, tareas: lista, factor: factorAplicado, adenda_de: adendaDe?.id || null }, { withCredentials: true });
      toast.success(adendaDe ? "Adenda creada" : "Contrato creado");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear el contrato"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[88vh] overflow-y-auto" data-testid="pagos-nuevo-contrato-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{adendaDe ? `Adenda al contrato con ${adendaDe.contratista?.nombre || ""}` : "Nuevo contrato con tercero"}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          {!adendaDe && (
            <div>
              <SelectBuscable value={tercerizadoId}
                onChange={v => { setTercerizadoId(v); const t = registro.find(x => x.id === v); if (t) { setNombre(t.razon_social); setRuc(t.ruc || ""); } }}
                options={registro.map(t => ({ value: t.id, label: `${t.razon_social}${t.ruc ? ` · RUC ${t.ruc}` : ""}` }))}
                vacio="Elegir del registro de tercerizados…" placeholderBuscar="Buscar tercerizado..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white" testId="pagos-contratista-registro" />
              <p className="text-[10px] text-gray-400 mt-1">Se registran en Tesorería → Tercerizados. Si todavía no está, escribí el nombre abajo.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input value={nombre} onChange={e => { setNombre(e.target.value); setTercerizadoId(""); }} placeholder="Nombre del contratista" disabled={!!adendaDe} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm disabled:bg-gray-50" data-testid="pagos-contratista-nombre" />
            <input value={ruc} onChange={e => { setRuc(e.target.value); setTercerizadoId(""); }} placeholder="RUC (opcional)" disabled={!!adendaDe} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm disabled:bg-gray-50" data-testid="pagos-contratista-ruc" />
          </div>
          {origenes.length > 1 && (
            <div className="flex gap-2 flex-wrap" data-testid="pagos-origen-chips">
              {["todos", ...origenes].map(o => (
                <button key={o} type="button" onClick={() => setOrigen(o)}
                  className={`px-3 py-1 rounded-xl text-xs font-semibold border ${origen === o ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-gray-600 border-gray-300"}`}
                  data-testid={`pagos-origen-${o}`}>{o === "todos" ? "Todas" : o}</button>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <p className="text-xs font-semibold text-gray-500 uppercase">Tareas a cubrir</p>
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar ítem, tarea o nivel..."
              className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs w-64" data-testid="pagos-buscar-tarea" />
          </div>
          {/* Fix DX (2026-09-23): seleccionar todas + factor multiplicador. */}
          <div className="flex items-center justify-between gap-2 flex-wrap bg-orange-50/40 border border-orange-100 rounded-xl px-3 py-2">
            <label className="flex items-center gap-2 text-xs text-gray-700 font-semibold">
              <input type="checkbox" checked={todasTildadas} onChange={toggleTodas} disabled={!seleccionables.length} data-testid="pagos-seleccionar-todas" />
              Seleccionar todas ({seleccionables.length})
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-600">Factor multiplicador</span>
              <input value={factor} onChange={e => setFactor(e.target.value)} placeholder="0,65" inputMode="decimal"
                className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-xs text-right bg-white" data-testid="pagos-factor" />
              <button type="button" onClick={aplicarFactor}
                className="text-xs font-semibold text-white bg-[#EA580C] hover:bg-[#C2410C] rounded-lg px-2.5 py-1" data-testid="pagos-aplicar-factor">
                Aplicar
              </button>
            </div>
            <p className="w-full text-[10px] text-gray-500">Monto cerrado de cada tarea = Disponible × factor. Después se puede corregir a mano cualquier línea.</p>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto border border-gray-100 rounded-xl p-2">
            {visibles.length === 0 && <p className="text-xs text-gray-400 text-center py-4">Sin tareas que coincidan.</p>}
            {coinciden.length > 300 && (
              <p className="text-[10px] text-gray-400 text-center pb-1" data-testid="pagos-tareas-recorte">
                Se muestran 300 de {coinciden.length} tareas. Usá el buscador para ver las demás; el total suma todas las tildadas.
              </p>
            )}
            {visibles.map(t => {
              const disponible = Number(t.fondo_disponible) || 0;
              const monto = Number(selected[t.id]) || 0;
              const pasa = (t.id in selected) && monto > disponible + 0.01;
              return (
                <div key={t.id} className="flex items-start gap-2 text-sm py-1 border-b border-gray-50 last:border-0" data-testid={`pagos-tarea-opt-${t.id}`}>
                  <input type="checkbox" className="mt-1" checked={t.id in selected} onChange={() => toggle(t.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-xs text-gray-800">
                      {t.item_numero} — {t.tarea_nombre}
                      {t.item_origen && t.item_origen !== "Base" && (
                        <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">{t.item_origen}</span>
                      )}
                    </p>
                    <p className="text-[10px] text-gray-400 truncate">
                      {t.nivel_label ? `${t.nivel_label} · ` : ""}{t.item_descripcion}
                    </p>
                    <p className="text-[10px] text-gray-500" data-testid={`pagos-tarea-disponible-${t.id}`}>
                      Disponible: <span className="font-semibold text-gray-700">{formatPYG(disponible)}</span>
                    </p>
                    {t.contrato_tercero && (
                      <p className="text-[10px] text-amber-700 font-semibold" data-testid={`pagos-tarea-en-contrato-${t.id}`}>
                        Ya está en el contrato con {t.contrato_tercero}
                      </p>
                    )}
                    {pasa && (
                      <p className="text-[10px] text-red-600 font-bold" data-testid={`pagos-tarea-excede-${t.id}`}>
                        ⚠ Supera el fondo disponible de la tarea
                      </p>
                    )}
                  </div>
                  {t.id in selected && (
                    <MontoInput value={selected[t.id]} onChange={v => setSelected(prev => ({ ...prev, [t.id]: v }))}
                      placeholder="Monto cerrado"
                      className={`w-32 border rounded px-1.5 py-1 text-xs ${pasa ? "border-red-300 bg-red-50/40" : "border-gray-200"}`}
                      data-testid={`pagos-tarea-monto-${t.id}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-xs bg-gray-50 rounded-xl px-3 py-2" data-testid="pagos-contrato-totales">
            <span className="text-gray-500">{elegidas.length} tarea(s) · Fondo disponible {formatPYG(fondoElegido)}</span>
            <span className={`font-bold ${excede ? "text-red-600" : "text-gray-800"}`}>Total del contrato: {formatPYG(totalContrato)}</span>
          </div>
          {excede && (
            <p className="flex items-center gap-1.5 text-[11px] text-red-600 font-semibold" data-testid="pagos-contrato-excede">
              <AlertTriangle size={13} /> El contrato supera el fondo de M.O. disponible de las tareas elegidas. Se puede guardar igual, pero queda en rojo.
            </p>
          )}
        </div>
        <button onClick={submit} disabled={saving} className="w-full mt-5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2" data-testid="pagos-crear-contrato-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {adendaDe ? "Crear adenda" : "Crear contrato"}
        </button>
      </div>
    </div>, document.body
  );
}
